<script>
   /****************************************************************/
   /* PlotScores                                                   */
   /* --------------------                                         */
   /* shows plot with PCA scores                                   */
   /****************************************************************/

   import { ColormapLegend, Segments } from 'svelte-plots-basic/2d';
   import { Vector, vector } from 'mdatools/arrays';

   import { getAxisTitle, getColorGroups, AXIS_COLOR, MAX_LABEL_SYMBOLS } from './Defaults.js';
   import { pcares_get } from '../wasm/dist/pca.js';

   import Axes from './Axes.svelte';
   import PlotSeries from './PlotSeries.svelte';
   import HotellingEllipse from './HotellingEllipse.svelte';
   import ConfidenseEllipses from './ConfidenseEllipses.svelte';


   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
      alpha = 0.05,                  // confidence level for Hotelling ellipse
   } = $props();

   // get the parameters needed for the plot
   let data = $derived(props && props.data ? props.data : null);
   let res = $derived(props && props.res ? props.res : null);
   let resobj = $derived(params && params.resID ? res[params.resID] : null);
   let dataset = $derived(params && params.resID ? data[params.resID] : null);

   /**
    * Computes min, max values for axis limits as well as 10% of their difference for x or y axis.
    *
    * @param showEllipse - logical, show or not Hotelling ellips.
    * @param confEllipse - string, confidence level for the confidence ellips (or 'none').
    * @param rangeS - range for score values.
    * @param rangeE - range for Hotelling ellipse coordinates.
    * @param rangeCE - range for confidence ellipse coordinates.
    */
   function getLim(showEllipse, confEllipse, rangeS, rangeE, rangeCE) {
      if (!rangeS) return null;

      // compute ranges for score values
      let vMin = rangeS[0];
      let vMax = rangeS[1];

      // adjust ranges if T2 ellipse is shown
      if (showEllipse && rangeE) {
         vMin = Math.min(vMin, rangeE[0]);
         vMax = Math.max(vMax, rangeE[1]);
      }

      // adjust ranges if confidence ellipses are shown
      if (confEllipse !== 'none' && rangeCE) {
         vMin = Math.min(vMin, rangeCE[0]);
         vMax = Math.max(vMax, rangeCE[1]);
      }

      // compute limits based on the ranges
      const dv = (vMax - vMin) * 0.10;
      return {min: vMin, max: vMax, d: dv};
   }

   /**
    * Gets vector with values for particular outcome and component from the result object
    * @param {Object} r - result object.
    * @param {Number} a - component index (from 1);
    */
   function getValues(r, a) {
      if (!r || !a || a < 1 || a > r.ncomp) return;
      return new Vector(pcares_get(r, 'scores', a))
   }

   // ranges of values to define coordinate limits
   let xRangeS = $state(), yRangeS = $state();     // scores
   let xRangeE = $state(), yRangeE = $state();     // hotelling ellipse
   let xRangeCE = $state(), yRangeCE = $state();   // confidence ellipse


   // reactive expression to generate x-indices for line and bar plots
   let rowIndices = $derived(res ? vector(resobj.objindices) : null);
   let labels = $derived(resobj ? resobj.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);

   // reactive expression to track chenges in "res" and "comp[0]" (x-axis)
   let compXValues = $derived(getValues(resobj, params.comp1));
   let xValues = $derived(params.plotType === 'p' ? compXValues : rowIndices);

   // reactive expression to track chenges in "reobj" and "comp[1]" (y-axis)
   let compY = $derived(params.comp2 ? params.comp2 : null);
   let yValues = $derived(getValues(resobj, compY));


   // reactive expression to get colors of the elements
   //let collabels, colmap, colbreaks, objcolors, colorVarName;
   let col = $derived(getColorGroups(dataset, resobj, params.colorBy, params.colorByVar, params.resID));

   // reactive expression to generate axis labels
   let xLabel = $derived(params.plotType === 'p' ? getAxisTitle(resobj, params.comp1) : 'Objects');
   let yLabel = $derived(getAxisTitle(resobj, params.comp2));

   // reactive expression to compute x-axis limits
   let lx = $derived(getLim(params.showEllipse, params.confEllipse, xRangeS, xRangeE, xRangeCE));
   let limX = $derived(lx ? [lx.min - lx.d, lx.max + lx.d] : [0, 1]);

   // reactive expression to compute x-axis limits
   let ly = $derived(getLim(params.showEllipse, params.confEllipse, yRangeS, yRangeE, yRangeCE));
   let limY = $derived(ly ?[ly.min - ly.d, ly.max + ly.d * (1 + (params.colorBy !== 'none'))] : [0, 1]);

   // reactive expression to generate plot title
   let title = $derived(
      (params.plotType ===  'l' || params.colorBy === 'none' || !col) ? 'Scores' :
      params.colorBy ==='groups' ? 'Scores (colored by groups)' :
         'Scores (colored by ' + col.varname + (dataset.varvaluesUnits ? ' ' + dataset.varvaluesUnits : '') + ')'
   );
</script>

{#if xValues && yValues }
<Axes {title} {limX} {limY} {xLabel} {yLabel} fileName='plot-scores' onclick={callbacks.unselectObject}>

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   {#if params.plotType === 'p'}
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />
   {/if}

   <!-- T2 ellipse -->
   {#if params.showEllipse && params.plotType === 'p'}
   <HotellingEllipse
      {alpha}
      {resobj}
      comp={[params.comp1, params.comp2]}
      bind:xRange={xRangeE}
      bind:yRange={yRangeE}
   />
   {/if}

   {#if params.colorBy === 'groups' && params.confEllipse !== 'none' && params.plotType === 'p' && col}
   <ConfidenseEllipses
      {xValues}
      {yValues}
      confLevel={Number.parseInt(params.confEllipse)/100}
      groups={resobj.classes}
      groupNames={resobj.classNames}
      groupColors={col.map}
      bind:xRange={xRangeCE}
      bind:yRange={yRangeCE}
   />
   {/if}

   <!-- plot series  -->
   <PlotSeries
      {xValues}
      {yValues}
      {labels}
      {selected}
      {callbacks}
      plotType={params.plotType}
      showLabels={params.showLabels}
      colors={col.colors}
      bind:xRange={xRangeS}
      bind:yRange={yRangeS}
   />


   <!-- colormap legend-->
   {#if col}
   <ColormapLegend show={col.breaks !== null} colmap={col.map} breaks={col.breaks} labels={col.labels}/>
   {/if}
</Axes>
{/if}

